import { useState, useMemo } from 'react';
import { Search, MapPin, Building2, ShieldCheck, List, Map as MapIcon, ExternalLink, Star } from 'lucide-react';
import { useVenues } from '../../hooks/useAPI';
import { CardSkeleton } from './Skeleton';
import FavoriteButton from './FavoriteButton';
import { FilterPanel, FilterPillRow } from './FilterPills';

interface ClubsScreenProps {
  onNavigate: (screen: any, params?: any) => void;
}

type SurfaceFilter = 'any' | 'hard' | 'clay' | 'grass' | 'astro-turf' | 'carpet';
type PriceFilter   = 'any' | 'lt100' | '100-200' | 'gt200';

const SURFACE_OPTIONS: { value: SurfaceFilter; label: string }[] = [
  { value: 'any',        label: 'Any surface' },
  { value: 'hard',       label: 'Hard' },
  { value: 'clay',       label: 'Clay' },
  { value: 'grass',      label: 'Grass' },
  { value: 'astro-turf', label: 'Astro-turf' },
  { value: 'carpet',     label: 'Carpet' },
];

const PRICE_OPTIONS: { value: PriceFilter; label: string; matches: (price: number | null) => boolean }[] = [
  { value: 'any',     label: 'Any price',           matches: () => true },
  { value: 'lt100',   label: 'Under AED 100',       matches: (p) => p != null && p < 100 },
  { value: '100-200', label: 'AED 100–200',         matches: (p) => p != null && p >= 100 && p <= 200 },
  { value: 'gt200',   label: 'AED 200+',            matches: (p) => p != null && p > 200 },
];

export default function ClubsScreen({ onNavigate }: ClubsScreenProps) {
  const { data: venues, isLoading } = useVenues();
  const [q, setQ] = useState('');
  const [view, setView] = useState<'list' | 'map'>('list');
  const [surface, setSurface] = useState<SurfaceFilter>('any');
  const [price, setPrice] = useState<PriceFilter>('any');

  // One row per venue, filtered by query + surface + price.
  // Groups for visual brand banding are computed in `groups` below.
  // We keep `rows` flat for the map view which works on individual
  // venue coordinates.
  const rows = useMemo(() => {
    const list = (venues as any[]) || [];
    const s = q.trim().toLowerCase();
    const priceMatcher = PRICE_OPTIONS.find((p) => p.value === price)!.matches;
    return list.filter((v) => {
      if (s && !(
        v.name?.toLowerCase().includes(s)
        || v.address?.toLowerCase().includes(s)
        || v.clubs?.name?.toLowerCase().includes(s)
      )) return false;
      if (surface !== 'any' && !(v.surfaces ?? []).includes(surface)) return false;
      if (!priceMatcher(v.min_price ?? null)) return false;
      return true;
    });
  }, [venues, q, surface, price]);

  // Group venues by brand and render ONE card per brand.
  // - Single-venue brands route straight to that venue.
  // - Multi-venue brands route to the brand overview (location list)
  //   so the customer picks a location before landing on the booking
  //   surface — matches the "Deuce > location list > booking page"
  //   navigation spec.
  // Aggregations for the card: cheapest visible venue price + union of
  // surfaces + cover photo from any venue with photos.
  const groups = useMemo(() => {
    const map = new Map<string, {
      club: any;
      venues: any[];
      minPrice: number | null;
      surfaces: Set<string>;
      cover: string | null;
      rating: number;
      reviews: number;
    }>();
    for (const v of rows) {
      const clubId = v.clubs?.id ?? v.club_id;
      let g = map.get(clubId);
      if (!g) {
        g = {
          club: v.clubs ?? { id: v.club_id, name: '—' },
          venues: [],
          minPrice: null,
          surfaces: new Set<string>(),
          cover: null,
          rating: Number(v.brand_avg_rating ?? 0),
          reviews: Number(v.brand_review_count ?? 0),
        };
        map.set(clubId, g);
      }
      g.venues.push(v);
      const p = v.min_price == null ? null : Number(v.min_price);
      if (p != null) g.minPrice = g.minPrice == null ? p : Math.min(g.minPrice, p);
      for (const s of (v.surfaces ?? [])) g.surfaces.add(s);
      if (!g.cover && Array.isArray(v.photos) && v.photos.length > 0) g.cover = v.photos[0];
    }
    return Array.from(map.values());
  }, [rows]);

  return (
    <div className="w-full min-h-screen bg-background pb-24">
      <div className="bg-gradient-to-br from-[#0F1E36] to-[#16294A] text-white px-6 pt-6 pb-6 rounded-b-[2rem]">
        <h1 className="text-2xl mb-1">Tennis clubs</h1>
        <p className="text-sm opacity-80">
          Browse facilities across Dubai — book a court, find a coach, or both.
        </p>
      </div>

      <div className="px-6 -mt-4 mb-4">
        <div className="bg-card border border-border rounded-2xl p-3 flex items-center gap-2 shadow-sm">
          <Search className="w-4 h-4 text-muted-foreground" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by club name or area"
            className="flex-1 bg-transparent text-sm outline-none"
          />
        </div>
      </div>

      <div className="px-6 mb-4 flex gap-2">
        <button
          onClick={() => setView('list')}
          className={`flex-1 h-9 rounded-full text-xs flex items-center justify-center gap-1.5 transition-colors ${
            view === 'list' ? 'bg-[#1A6B4F] text-white' : 'bg-card border border-border'
          }`}
        >
          <List className="w-3.5 h-3.5" /> List
        </button>
        <button
          onClick={() => setView('map')}
          className={`flex-1 h-9 rounded-full text-xs flex items-center justify-center gap-1.5 transition-colors ${
            view === 'map' ? 'bg-[#1A6B4F] text-white' : 'bg-card border border-border'
          }`}
        >
          <MapIcon className="w-3.5 h-3.5" /> Map
        </button>
      </div>

      {/* Filter pills — surface and price band. Hidden in map view
          where the iframe takes up most of the screen and filters
          would feel cramped. */}
      {view === 'list' && (
        <div className="px-6 mb-4">
          <FilterPanel
            activeCount={(surface !== 'any' ? 1 : 0) + (price !== 'any' ? 1 : 0)}
            onClearAll={() => { setSurface('any'); setPrice('any'); }}
          >
            <FilterPillRow
              label="Surface"
              defaultValue="any"
              options={SURFACE_OPTIONS}
              value={surface}
              onChange={(v) => setSurface(v as SurfaceFilter)}
            />
            <FilterPillRow
              label="Price band"
              defaultValue="any"
              options={PRICE_OPTIONS}
              value={price}
              onChange={(v) => setPrice(v as PriceFilter)}
            />
          </FilterPanel>
        </div>
      )}

      {view === 'map' && <ClubsMap rows={rows} onNavigate={onNavigate} />}

      <div className={view === 'map' ? 'hidden' : 'px-6 space-y-5'}>
        {isLoading ? (
          <>
            <CardSkeleton />
            <CardSkeleton />
            <CardSkeleton />
          </>
        ) : rows.length === 0 ? (
          <div className="text-center text-sm text-muted-foreground py-12">
            {q ? 'No venues match your search.' : 'No venues live yet.'}
          </div>
        ) : (
          groups.map((g) => {
            const multi = g.venues.length > 1;
            const onlyVenue = g.venues[0];
            // Multi-venue brands → location list; single-venue → straight to that venue.
            const onClick = multi
              ? () => onNavigate('club', { id: g.club.id })
              : () => onNavigate('club', { id: g.club.id, venueId: onlyVenue.id });
            return (
              <button
                key={g.club.id}
                onClick={onClick}
                className="w-full text-left bg-card border border-border rounded-2xl overflow-hidden hover:border-[#1A6B4F]/40 transition-colors relative"
              >
                <div className="absolute top-3 right-3 z-10">
                  <FavoriteButton target={{ kind: 'club', id: g.club.id }} variant="floating" />
                </div>
                <div className="h-32 w-full bg-secondary overflow-hidden">
                  {g.cover ? (
                    <img src={g.cover} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                      <Building2 className="w-7 h-7" />
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-2">
                    {g.club.logo_url && (
                      <img src={g.club.logo_url} alt=""
                        className="w-6 h-6 rounded-md object-cover border border-border shrink-0" />
                    )}
                    <span className="font-medium truncate">{g.club.name}</span>
                    {g.club.accepts_freelance_coaches && (
                      <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-[#D4F04A]/20 text-[#1A6B4F] flex items-center gap-1 shrink-0">
                        <ShieldCheck className="w-3 h-3" />
                        Open
                      </span>
                    )}
                    {g.reviews > 0 && (
                      <span className="ml-auto inline-flex items-center gap-1 text-xs text-muted-foreground shrink-0">
                        <Star className="w-3 h-3 fill-[#D4F04A] text-[#D4F04A]" />
                        <span className="font-medium text-foreground">{g.rating.toFixed(1)}</span>
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1 mt-1.5 text-xs text-muted-foreground">
                    <MapPin className="w-3 h-3" />
                    {multi
                      ? <span>{g.venues.length} locations</span>
                      : <span className="truncate">{onlyVenue.address ?? onlyVenue.name}</span>}
                  </div>
                  {g.minPrice != null && (
                    <div className="text-xs mt-1.5">
                      <span className="text-[#1A6B4F] font-semibold">
                        From AED {Math.round(g.minPrice)}
                      </span>
                      <span className="text-muted-foreground"> / hour</span>
                    </div>
                  )}
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

type Coord = { lat: number; lng: number };

function coordOf(c: any): Coord | null {
  const raw = c?.coordinates;
  if (!raw) return null;
  const lat = Number(raw.lat ?? raw.latitude);
  const lng = Number(raw.lng ?? raw.longitude ?? raw.lon);
  if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
  return null;
}

/**
 * Map view: OpenStreetMap embed centered on the club bounding box.
 * Each mappable club shows a summary card below the map that opens a
 * full map view in a new tab. If we later want interactive markers
 * we can swap the iframe for a Leaflet canvas without changing the
 * surrounding layout.
 */
function ClubsMap({ rows, onNavigate }: { rows: any[]; onNavigate: (screen: any, params?: any) => void }) {
  // `rows` are venues. Each maps to one marker / one summary card.
  const mapped = rows.map((v) => ({ venue: v, coord: coordOf(v) }))
                     .filter((r): r is { venue: any; coord: Coord } => !!r.coord);

  // Default: Dubai bounding box.
  let minLat = 25.10, maxLat = 25.30, minLng = 55.15, maxLng = 55.45;
  if (mapped.length > 0) {
    const lats = mapped.map((r) => r.coord.lat);
    const lngs = mapped.map((r) => r.coord.lng);
    minLat = Math.min(...lats) - 0.03;
    maxLat = Math.max(...lats) + 0.03;
    minLng = Math.min(...lngs) - 0.03;
    maxLng = Math.max(...lngs) + 0.03;
  }
  const bbox = `${minLng},${minLat},${maxLng},${maxLat}`;
  const markerParam = mapped
    .slice(0, 1) // OSM embed only shows one marker in the iframe URL
    .map((r) => `&marker=${r.coord.lat},${r.coord.lng}`)
    .join('');
  const src = `https://www.openstreetmap.org/export/embed.html?bbox=${encodeURIComponent(bbox)}&layer=mapnik${markerParam}`;

  return (
    <div className="px-0 sm:px-6">
      <div className="w-full aspect-[4/3] sm:rounded-2xl overflow-hidden border-y sm:border border-border bg-secondary">
        <iframe
          title="Clubs map"
          src={src}
          className="w-full h-full"
          loading="lazy"
          referrerPolicy="no-referrer"
        />
      </div>

      <div className="px-6 mt-4 space-y-2">
        {mapped.length === 0 ? (
          <div className="text-center text-xs text-muted-foreground py-6">
            No venues have published map coordinates yet.
          </div>
        ) : (
          mapped.map(({ venue, coord }) => (
            <div key={venue.id} className="bg-card border border-border rounded-xl p-3 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#D4F04A]/20 flex items-center justify-center shrink-0">
                <Building2 className="w-4 h-4 text-[#1A6B4F]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">
                  {venue.clubs?.name ?? '—'}
                  {venue.clubs?.name && venue.name ? ` · ${venue.name}` : ''}
                </div>
                {venue.address && (
                  <div className="text-xs text-muted-foreground flex items-center gap-1 truncate">
                    <MapPin className="w-3 h-3" /> {venue.address}
                  </div>
                )}
              </div>
              <div className="flex flex-col items-end gap-1">
                <button
                  onClick={() => onNavigate('club', { id: venue.club_id, venueId: venue.id })}
                  className="h-8 px-3 rounded-lg bg-[#1A6B4F] text-white text-xs"
                >
                  View
                </button>
                <a
                  href={`https://www.openstreetmap.org/?mlat=${coord.lat}&mlon=${coord.lng}#map=15/${coord.lat}/${coord.lng}`}
                  target="_blank" rel="noopener noreferrer"
                  className="text-[11px] text-muted-foreground hover:text-[#1A6B4F] flex items-center gap-1"
                >
                  <ExternalLink className="w-3 h-3" /> Directions
                </a>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
